"use server";

import { prisma } from "@/lib/db";
import { getGuest } from "@/lib/auth";
import { env } from "@/lib/env";

export async function me() {
  const user = await getGuest();
  if (!user) return { user: null, google: Boolean(env().googleClientId), demo: env().demoMode };
  const [inbox, stays, reviews, promos, unread] = await Promise.all([
    prisma.inboxItem.findMany({ where: { userId: user.id }, orderBy: { createdAt: "desc" }, take: 30 }),
    prisma.booking.findMany({
      where: { guestId: user.id, depositPaidAt: { not: null } },
      include: { listing: { include: { photos: { take: 1 } } }, review: true },
      orderBy: { checkIn: "desc" },
    }),
    prisma.review.findMany({
      where: { userId: user.id },
      include: { listing: true },
      orderBy: { createdAt: "desc" },
    }),
    prisma.promoCode.findMany({ where: { active: true } }),
    prisma.inboxItem.count({ where: { userId: user.id, readAt: null } }),
  ]);
  return {
    user: { id: user.id, name: user.name, email: user.email, phone: user.phone, image: user.image },
    unread,
    inbox: inbox.map((i) => ({
      id: i.id,
      title: i.title,
      body: i.body,
      href: i.href,
      read: Boolean(i.readAt),
      createdAt: i.createdAt.toISOString(),
    })),
    stays: stays.map((s) => ({
      id: s.id,
      title: s.listing.title,
      photo: s.listing.photos[0]?.url ?? null,
      checkIn: s.checkIn,
      checkOut: s.checkOut,
      status: s.status,
      hasReview: Boolean(s.review),
      totalAmount: s.totalAmount,
    })),
    reviews: reviews.map((r) => ({
      id: r.id,
      listing: r.listing.title,
      rating: r.rating,
      comment: r.comment,
      createdAt: r.createdAt.toISOString(),
    })),
    promos: promos.map((p) => ({
      code: p.code,
      description: p.description,
      percentOff: p.percentOff,
    })),
    google: Boolean(env().googleClientId),
    demo: env().demoMode,
  };
}

export async function markInboxRead(id?: string) {
  const user = await getGuest();
  if (!user) return { ok: false };
  if (id) {
    await prisma.inboxItem.updateMany({ where: { id, userId: user.id }, data: { readAt: new Date() } });
  } else {
    await prisma.inboxItem.updateMany({ where: { userId: user.id, readAt: null }, data: { readAt: new Date() } });
  }
  return { ok: true };
}

export async function subscribePush(sub: { endpoint: string; keys: { p256dh: string; auth: string } }) {
  const user = await getGuest();
  if (!user) return { ok: false as const, error: "Sign in to continue." };
  await prisma.pushSubscription.upsert({
    where: { endpoint: sub.endpoint },
    create: { userId: user.id, endpoint: sub.endpoint, p256dh: sub.keys.p256dh, auth: sub.keys.auth },
    update: { userId: user.id, p256dh: sub.keys.p256dh, auth: sub.keys.auth },
  });
  return { ok: true as const };
}

export async function latestOutbox() {
  if (!env().demoMode) return { items: [] as never[] };
  const items = await prisma.outbox.findMany({
    orderBy: { createdAt: "desc" },
    take: 6,
  });
  return {
    items: items.map((i) => ({
      id: i.id,
      to: i.to,
      subject: i.subject,
      body: i.body,
      createdAt: i.createdAt.toISOString(),
    })),
  };
}
